import gsap from 'gsap'
import { useGSAP } from '@gsap/react'
import { useEffect, useRef, useState } from 'react'
import { uidFactory } from '@10000/common'
import { useDelayedValue, useView } from '@10000/react'
import { Body, Box, CaretDownCircle, CaretUpCircle, Drawer, H4 } from '../widgets'
import { Form, Radio, Submit, useForm } from '../forms'
import { useAnalyticsData } from './AnalyticsData.jsx'
import { industries, levels, roles } from '../../lookups'
import './FiltersDrawer.scss'

const uid = uidFactory()

export const FiltersDrawer = ({ open, onClose }) => {
  const { teams = [] } = useView('tenant')
  const { mode, filters, setFilters, lookups, compareConsensus } = useAnalyticsData()
  const [key, setKey] = useState()
  // "refreshing" the form using the key prop breaks opening the drawer unless we delay a little
  const drawerOpen = useDelayedValue(open, { true: 20 })

  const teamFilterItems = teams
    .filter(({ name, squizCount }) => !!name && squizCount > 0)
    .map(({ id, name }) => ({ id, label: name }))

  const applyFilters = filters => {
    setFilters(filters)
    onClose()
  }

  const mapLookup = (lookup, values) => values.map(id => ({ id, label: lookup[id].name }))

  useEffect(() => {
    if(open) {
      setKey(uid())
    }
  }, [open])

  return (
    <Form key={key} onSubmit={applyFilters} values={filters}>
      <Drawer
        open={drawerOpen}
        onClose={onClose}
        color="black"
        className="filters-drawer"
      >
        <H4 color="black" mb={8}>
          Filters
        </H4>

        <Box grow overflow="hidden" fullWidth>
          <Box column maxHeight="100%" overflow="auto" fullWidth>
            {teamFilterItems.length > 1 &&
              <Filter
                name="teamId"
                heading="Team"
                items={teamFilterItems}
              />
            }
            {mode === 'compare' && !compareConsensus && <>
              <Filter
                name="industry"
                heading="Industry"
                items={mapLookup(industries, lookups.industries)}
                allowEmpty
              />
              <Filter
                name="role"
                heading="Team Function"
                items={mapLookup(roles, lookups.roles)}
                allowEmpty
              />
              <Filter
                name="level"
                heading="Team Level"
                items={mapLookup(levels, lookups.levels)}
                allowEmpty
              />
            </>}
          </Box>
        </Box>

        <Box
          background="white"
          fullWidth
          p="12px 24px"
          mx={-24}
          sizing="content-box"
          style={{ borderTop: '1px solid black' }}
        >
          <Box
            width={452}
            gap={24}
            alignItems="center"
            justifyContent="space-between"
          >
            <ClearAllButton />
            <Submit fullWidth>Apply</Submit>
          </Box>
        </Box>
      </Drawer>
    </Form>
  )
}

const Filter = ({ name, heading, items, allowEmpty, disabled }) => {
  const { contextSafe } = useGSAP()
  const { setFieldValue } = useForm()
  const [expanded, setExpanded] = useState(true)
  const list = useRef()

  const clearFilter = () => setFieldValue(name, '')

  const toggleExpanded = contextSafe(() => setExpanded(expanded => {
    gsap.to(list.current, { height: expanded ? 0 : 'auto' })
    return !expanded
  }))

  return (
    <Box
      column
      fullWidth
      gap={16}
      p={expanded ? '8px 0 20px 0' : '8px 0 0 0'}
      style={{ borderTop: '1px solid black' }}
    >
      <Heading
        heading={heading}
        disabled={disabled}
        onClear={allowEmpty ? clearFilter : undefined}
        expanded={expanded}
        onExpandedToggled={toggleExpanded}
      />

      <Box column ref={list} gap={16} overflow="hidden">
        {items.map(({ label, id }) =>
          <Box key={id} py={6}>
            <Radio
              dark
              name={name}
              value={id}
              label={label}
              disabled={disabled}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

const Heading = ({ heading, disabled, onClear, expanded, onExpandedToggled }) => (
  <Box
    fullWidth
    height={48}
    px={16}
    gap={16}
    background={disabled ? 'var(--grey)' : 'var(--blue)'}
    alignItems="center"
  >
    <Box grow fullWidth>
      <Body alt bold color="white">
        {heading}{disabled && ' - Coming soon!'}
      </Body>
    </Box>

    {onClear &&
      <Body alt bold italic underline color="white" style={{ cursor: 'pointer' }} onClick={onClear}>
        Clear
      </Body>
    }

    <Box onClick={onExpandedToggled} cursor="pointer">
      {expanded ? <CaretUpCircle /> : <CaretDownCircle />}
    </Box>
  </Box>
)

const ClearAllButton = () => {
  const { setFieldValue } = useForm()

  const clearAll = () => {
    setFieldValue('industry', '')
    setFieldValue('role', '')
    setFieldValue('level', '')
  }

  return (
    <Body alt bold italic underline style={{ cursor: 'pointer' }} onClick={clearAll} noWrap>
      Clear all
    </Body>
  )
}